import {memo} from 'react'
import {useParams} from 'react-router-dom'
import {differenceInCalendarMonths, parseISO} from 'date-fns'
import {TinyColumn} from '@ant-design/plots'
import {Space, Table} from 'antd'

import styles from './RequestForecast.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {
  Block,
  Text,
  Button,
  Modal,
  Loader,
  UserAvatar,
  IconButton,
  IconTitle,
  RotateArrowFourthIcon,
  BlackboardChartFourthIcon,
  DotsIcon,
} from 'common/components'
import {useGetForecast} from 'common/api'
import {
  formatDate,
  formatMoney,
  maybePluralize,
  isSameYearMonth,
  lastUpdatedItemFromArray,
  totalNumberFromArray,
  arrayFromMonthsCount,
  isEqual,
  isItemDrawdown,
} from 'common/helpers'
import {ForecastType, ForecastItem} from 'common/types'
import {RequestForecastForm, useGetRequest, isMilestonePassed} from '..'

interface ForecastProps {
  creationDisabled: boolean
  forecast: ForecastItem[]
  request: any
  onForecastOpen: () => void
}

interface SummaryRowProps {
  type: ForecastType
  forecast: ForecastItem[]
}

interface ChartProps {
  months: number
  startDate: Date
  forecast: ForecastItem[]
}

export default function RequestForecast() {
  const params = useParams()
  const {data: request, isRefetching: isRequestRefetching} = useGetRequest(
    params.uuid
  )
  const {data: forecast, isRefetching: isForecastRefetching} = useGetForecast(
    request?.project?.uuid, !!request?.project?.uuid
  )
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const isForecastOpen = useStore((state) => state.isForecastOpen)
  const setForecastOpen = useStore((state) => state.setForecastOpen)

  const handleForecastOpen = () => setForecastOpen(true)

  const handleForecastClose = () => setForecastOpen(false)

  const creationDisabled =
    !permissions?.includes('edit forecast') || isForecastRefetching || isRequestRefetching

  return (
    <>
      <Block relative type="white">
        {!isMilestonePassed('forecast-created', request) ? (
          <div className={styles.section}>
            <IconTitle icon={<BlackboardChartFourthIcon />}>Forecast</IconTitle>
            { !creationDisabled &&
              (!!permissions?.includes('edit loan request (lm check)')) &&
              <Button
                variant="secondary"
                disabled={creationDisabled}
                onClick={handleForecastOpen}
              >
                create
              </Button>
          }
          </div>
        ) : forecast?.length ? (
          <Forecast
            creationDisabled={creationDisabled}
            forecast={forecast}
            request={request}
            onForecastOpen={handleForecastOpen}
          />
        ) : (
          <Loader />
        )}
      </Block>
      <Modal
        title={`‘${request?.project?.title || ''}’ Forecast`}
        width={692}
        open={isForecastOpen}
        onCancel={handleForecastClose}
      >
        <RequestForecastForm
          request={request}
          forecast={forecast}
          onClose={handleForecastClose}
        />
      </Modal>
    </>
  )
}

const Forecast = memo(
  ({creationDisabled, forecast, request, onForecastOpen}: ForecastProps) => {
    const permissions: PermissionType[] = useStore((state) => state.permissions)
    const lastUpdatedItem = lastUpdatedItemFromArray(forecast)
    const updatedBy = lastUpdatedItem.value?.createdBy
    const startDate = parseISO(forecast[0].date)
    const endDate = parseISO(forecast.at(-1).date)
    const months = differenceInCalendarMonths(endDate, startDate)

    return (
      <>
        { !creationDisabled && 
          (!!permissions?.includes('edit loan request (lm check)')) &&
          (
          <IconButton className={styles.edit} onClick={onForecastOpen}>
            <DotsIcon />
          </IconButton>
        )}
        <div className={styles.header}>
          <IconTitle icon={<BlackboardChartFourthIcon />} mb={16}>
            Forecast
          </IconTitle>
          <div className={styles.block}>
            <Space size={12}>
              <UserAvatar
                src={updatedBy?.photo}
                size={32}
                alt={updatedBy?.firstName}
              />
              <Text as="span" weight="medium" size="sm" color="grey">
                has been updated{' '}
                {formatDate(lastUpdatedItem.updatedAt, 'third')}
                <br />
                by{' '}
                {updatedBy
                  ? `${updatedBy.firstName} ${updatedBy.lastName}`
                  : ''}
              </Text>
            </Space>
            <Chart months={months} startDate={startDate} forecast={forecast} />
          </div>
        </div>
        <Table
          className={styles.table}
          showHeader={false}
          pagination={false}
          columns={columns}
          dataSource={getForecastData(forecast)}
          summary={() => (
            <>
              <SummaryRow type="drawdown" forecast={forecast} />
              <SummaryRow type="repayment" forecast={forecast} />
            </>
          )}
        />
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const SummaryRow = ({type, forecast}: SummaryRowProps) => {
  const filteredItems = forecast.filter((item) => item.type === type)

  if (!filteredItems.length) return null

  const items = filteredItems.map((subItem) => subItem.value)
  const totalAmount = totalNumberFromArray(items, 'amount')
  const isDrawdown = isItemDrawdown({type})
  const title = maybePluralize(items.length, type)

  return (
    <Summary.Row>
      <Summary.Cell index={0} />
      <Summary.Cell index={1}>
        {isDrawdown ? (
          title
        ) : (
          <Space>
            <RotateArrowFourthIcon />
            {title}
          </Space>
        )}
      </Summary.Cell>
      <Summary.Cell index={2} colSpan={2} align="right">
        <Text size="3xl" weight="medium">
          {formatMoney(totalAmount)}
        </Text>
      </Summary.Cell>
    </Summary.Row>
  )
}

const Chart = memo(
  ({months, startDate, forecast}: ChartProps) => {
    const monthsArray = arrayFromMonthsCount(months, startDate)
    const drawdowns = forecast.filter((item) => isItemDrawdown(item))
    const data: number[] = []
    monthsArray.forEach((date) => {
      data.push(
        drawdowns.some((item) => isSameYearMonth(item.value.date, date))
          ? totalNumberFromArray(
              drawdowns
                .filter((item) => isSameYearMonth(item.value.date, date))
                .map((item) => item.value.amount)
            )
          : 0
      )
    })

    const config: any = {
      data,
      width: 148,
      height: 38,
      color: '#b4d8d8',
      maxColumnWidth: 4,
      columnStyle: ({y}: any) =>
        y ? {radius: 6, stroke: '#569695'} : undefined,
    }

    return <TinyColumn {...config} />
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
)

const {Summary} = Table

const getForecastData = (forecast: ForecastItem[]) => {
  let drawdownCount = 0
  let repaymentCount = 1
  return forecast.map((item, index) => ({
    ...item.value,
    key: item.uuid,
    number: isItemDrawdown(item) ? drawdownCount++ : repaymentCount++,
    type: item.type,
    date: parseISO(item.value.date),
    prevDate:
      index === 0 ? undefined : parseISO(forecast[index - 1].value.date),
  }))
}

const columns = [
  {
    dataIndex: 'date',
    render: (value: Date, record: any) => {
      if (isSameYearMonth(record.prevDate, value))
        return <div className={styles.line} />
      return formatDate(value, 'fourth')
    },
  },
  {
    dataIndex: 'type',
    render: (value: ForecastType, record: any) => {
      const content =
        value !== 'repayment' && record.number === 0
          ? 'Initial drawdown'
          : `Est. ${value} ${record.number}`
      return value === 'repayment' ? (
        <Space>
          <RotateArrowFourthIcon />
          <span>{content}</span>
        </Space>
      ) : (
        content
      )
    },
  },
  {
    dataIndex: 'date',
    render: (value: Date) => (
      <Text size="xs" color="grey" align="right">
        {formatDate(value, 'third')}
      </Text>
    ),
  },
  {
    dataIndex: 'amount',
    render: (value: number, record: any) => {
      const content = formatMoney(value, {prefix: false})
      return (
        <Text weight="demiBold" align="right">
          {record.type === 'repayment' ? (
            <>
              <Text as="span" color="grey">
                (
              </Text>
              {content}
              <Text as="span" color="grey">
                )
              </Text>
            </>
          ) : (
            content
          )}
        </Text>
      )
    },
  },
]
